import React, { useState } from 'react';

/**
 * Filesystem Section
 *
 * Visualizes the proposed data architecture with:
 * - Interactive file tree
 * - Access control model
 * - Metadata schema
 */
const FilesystemSection = ({ content }) => {
  return (
    <div className="df-filesystem">
      <header className="df-section__header">
        <h2 className="df-section__title">{content.title}</h2>
        <p className="df-section__subtitle">{content.subtitle}</p>
      </header>

      {/* Description */}
      <p className="df-gap-card__description" style={{ marginBottom: '2rem' }}>
        {content.structureDescription}
      </p>

      {/* File Tree Visualization */}
      <div style={{ marginBottom: '3rem' }}>
        <h3 className="df-flow-diagram__title">Proposed Directory Structure</h3>
        <FileTree structure={content.fileStructure} />
      </div>

      {/* Access Control */}
      <div style={{ marginBottom: '3rem' }}>
        <h3 className="df-flow-diagram__title">{content.accessControl.title}</h3>
        <p className="df-gap-card__description" style={{ marginBottom: '1rem' }}>
          {content.accessControl.description}
        </p>
        <div className="df-access-grid">
          {content.accessControl.roles.map((role) => (
            <AccessCard key={role.role} role={role} />
          ))}
        </div>
      </div>

      {/* Metadata Schema */}
      <div>
        <h3 className="df-flow-diagram__title">{content.metadataSchema.title}</h3>
        <p className="df-gap-card__description" style={{ marginBottom: '1rem' }}>
          {content.metadataSchema.description}
        </p>
        <MetadataSchema fields={content.metadataSchema.fields} />
      </div>
    </div>
  );
};

/**
 * File Tree Component - Recursive tree visualization
 */
const FileTree = ({ structure, depth = 0 }) => {
  const [expanded, setExpanded] = useState(true);
  const isFolder = structure.type === 'folder';
  const hasChildren = structure.children && structure.children.length > 0;

  return (
    <div className="df-file-tree">
      <FileTreeItem
        structure={structure}
        depth={depth}
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
      />
      {isFolder && hasChildren && expanded && (
        <div>
          {structure.children.map((child, index) => (
            <FileTreeItem
              key={index}
              structure={child}
              depth={depth + 1}
              expanded={true}
              onToggle={() => {}}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Single file tree item
 */
const FileTreeItem = ({ structure, depth, expanded, onToggle }) => {
  const [childExpanded, setChildExpanded] = useState(depth < 3);
  const isFolder = structure.type === 'folder';
  const hasChildren = structure.children && structure.children.length > 0;
  const indent = depth * 20;

  return (
    <>
      <div
        className="df-file-tree__item"
        style={{ paddingLeft: `${indent}px` }}
        onClick={hasChildren ? () => setChildExpanded(!childExpanded) : undefined}
        role={hasChildren ? 'button' : undefined}
      >
        <span className={`df-file-tree__icon df-file-tree__icon--${structure.type}`}>
          {isFolder ? (
            hasChildren ? (
              childExpanded ? (
                <FolderOpenIcon />
              ) : (
                <FolderIcon />
              )
            ) : (
              <FolderIcon />
            )
          ) : (
            <FileIcon />
          )}
        </span>
        <span className={`df-file-tree__name ${isFolder ? 'df-file-tree__name--folder' : ''}`}>
          {structure.name}
        </span>
        <span className="df-file-tree__description">
          {structure.description}
        </span>
      </div>
      {isFolder && hasChildren && childExpanded && (
        <>
          {structure.children.map((child, index) => (
            <FileTreeItem
              key={index}
              structure={child}
              depth={depth + 1}
              expanded={true}
              onToggle={() => {}}
            />
          ))}
        </>
      )}
    </>
  );
};

/**
 * Access Control Card
 */
const AccessCard = ({ role }) => {
  return (
    <div
      className="df-access-card"
      style={{ '--card-color': role.color }}
    >
      <div className="df-access-card__header">
        <div className="df-access-card__icon" style={{ color: role.color }}>
          <RoleIcon name={role.icon} />
        </div>
        <span className="df-access-card__role">{role.role}</span>
      </div>
      <p className="df-access-card__access">{role.access}</p>
      <p className="df-access-card__usecase">{role.useCase}</p>
    </div>
  );
};

/**
 * Metadata Schema Table
 */
const MetadataSchema = ({ fields }) => {
  return (
    <div className="df-file-tree" style={{ padding: '1rem' }}>
      <pre style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.6 }}>
        {`{`}
        {fields.map((field, index) => (
          <div key={field.name} style={{ paddingLeft: '1rem' }}>
            <span style={{ color: '#93c5fd' }}>"{field.name}"</span>
            <span style={{ color: '#94a3b8' }}>: </span>
            <span style={{ color: '#fbbf24' }}>"{field.type}"</span>
            <span style={{ color: '#94a3b8' }}>,</span>
            <span style={{ color: '#64748b', marginLeft: '1rem' }}>{/* // */}{field.description}</span>
          </div>
        ))}
        {`}`}
      </pre>
    </div>
  );
};

/**
 * Icons
 */
const FolderIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
  </svg>
);

const FolderOpenIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z"/>
  </svg>
);

const FileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const RoleIcon = ({ name }) => {
  const icons = {
    shield: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    users: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    user: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    )
  };

  return icons[name] || <span>?</span>;
};

export default FilesystemSection;
